// The Dec page step's fetch, in the Record board's words (spec C11, C7.4, C16): the progress line,
// the countdown while the code is awaited, why a fetch failed, and the stamps. Pure functions, so
// the step card and the tests read the same lines.
import type { FetchErrorKind, FetchStage, FetchView, FinishedFetch } from '../auth/api';

/** The progress line (spec C11). */
export const stageRows = [
  'Logging in',
  'Retrieving 2FA code',
  'Opening policy',
  'Downloading dec page',
  'Done',
] as const;

const stageIndex: Record<FetchStage, number> = {
  queued: -1,
  waiting_for_carrier: -1,
  logging_in: 0,
  waiting_for_code: 1,
  opening_policy: 2,
  downloading: 3,
  done: 5,
};

/** Each row of the progress line: done (green check), now (spinner) or still to come. */
export function progressRows(stage: FetchStage): { label: string; cls: '' | 'ok' | 'now' }[] {
  const at = stageIndex[stage];
  return stageRows.map((label, i) => ({
    label,
    cls: i < at ? 'ok' : i === at ? 'now' : '',
  }));
}

/**
 * A queued fetch whose carrier login is busy with another fetch (another record, the other
 * environment, or a run from the Mac): one browser per login at a time (docs/plans/shared-worker.md).
 */
export const waitingForCarrierLine = 'Another fetch on this carrier is running. Yours starts next.';

/** The "Fetching" tag's tooltip on a list row. */
export const fetchingTip = (waiting: boolean) =>
  waiting ? waitingForCarrierLine : 'Fetching the dec page from the carrier now.';

/** How long the portal waits for the emailed code (spec C11; a Setting later). */
export const codeWaitSeconds = 90;

/** Seconds left to wait for the code, counted from when the job reached that stage. */
export function codeSecondsLeft(stageAt: string, now: Date): number {
  const gone = Math.floor((now.getTime() - Date.parse(stageAt)) / 1000);
  return Math.max(0, Math.min(codeWaitSeconds, codeWaitSeconds - gone));
}

/** "0:38" */
export function mmss(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** A queued job that has not started in this long gets the "not started yet" line. */
export const slowStartSeconds = 120;

/** Why one attempt failed, in plain words (spec C16), for the step and the Details list. */
export function failureReason(
  kind: FetchErrorKind | null,
  carrier: string,
  started = true,
): string {
  if (!started) return 'The fetch did not start';
  switch (kind) {
    case 'no_code':
      return `No code from ${carrier} in 90 seconds`;
    case 'login_rejected':
      return `${carrier} rejected the login`;
    case 'not_found':
      return `${carrier} did not find this policy number`;
    case 'no_matching_document':
      return `${carrier} found this policy, but its dec page did not show the policy number`;
    case 'site_changed':
      return `The ${carrier} site did not look as expected`;
    case 'download_failed':
      return `${carrier} did not give the dec page`;
    case 'timeout':
      return 'The fetch took too long';
    case 'site_down':
      return `The ${carrier} site was down`;
    case 'not_configured':
      return `Fetching from ${carrier} is not set up here`;
    default:
      return 'Something went wrong during the fetch';
  }
}

/** The headline of a single failed attempt, with what to do next. */
export function failedOnceLine(kind: FetchErrorKind | null, carrier: string, started: boolean) {
  if (kind === 'no_code') {
    return `No code received from ${carrier} in 90 seconds. The code may arrive late. Try again in a minute, or get the dec page from the ${carrier} site.`;
  }
  return `${failureReason(kind, carrier, started)}. Try again, or get the dec page from the ${carrier} site.`;
}

/**
 * The line when the carrier's site was down (a maintenance page, or it did not load). That says
 * nothing about the renewal, so it is not an attempt and Fetch stays open.
 */
export function siteDownLine(carrier: string, at: string | null): string {
  const when = at ? `, ${stampTime(at)}` : '';
  return `The ${carrier} site was down when the fetch ran${when} (maintenance or not loading). This does not count as an attempt. Fetch again later.`;
}

/**
 * The line when the worker has no login or code mailbox for the carrier in this environment
 * (staging). Nothing was tried, so it is not an attempt; the dec page comes from the site instead.
 */
export function notConfiguredLine(carrier: string): string {
  return `Fetching from ${carrier} is not set up here. Get the dec page from the ${carrier} site, then use Upload PDF.`;
}

/** While the carrier rests after its site was down (15 minutes): when Fetch opens again. */
export function restingLine(carrier: string, until: string, queued: boolean): string {
  return queued
    ? `The ${carrier} site was down a moment ago. Your fetch starts at ${clockTime(until)}.`
    : `The ${carrier} site was down a moment ago. Fetch opens again at ${clockTime(until)}.`;
}

const clockFmt = new Intl.DateTimeFormat('en-US', {
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
  timeZone: 'America/New_York',
});

/** "14:35" in New York time. */
export const clockTime = (iso: string) => clockFmt.format(new Date(iso));

const timeFmt = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
  timeZone: 'America/New_York',
});

/** "Oct 26 14:20" in New York time. */
export function stampTime(iso: string): string {
  const parts = Object.fromEntries(
    timeFmt.formatToParts(new Date(iso)).map((p) => [p.type, p.value]),
  );
  return `${parts.month} ${parts.day} ${parts.hour}:${parts.minute}`;
}

type Doc = FetchView['documents'][number];

/** The step's stamp once the dec page is in: "Fetched from NYCM, Oct 26 14:20, 0:38". */
export function decStamp(doc: Doc, carrier: string | null): string {
  if (doc.source === 'fetch') {
    const took = doc.fetchSeconds === null ? '' : `, ${mmss(doc.fetchSeconds)}`;
    return `Fetched from ${carrier ?? 'the carrier'}, ${stampTime(doc.createdAt)}${took}`;
  }
  if (doc.source === 'email') return `From email, ${stampTime(doc.createdAt)}`;
  return `Uploaded by ${doc.by ?? 'someone'}, ${stampTime(doc.createdAt)}`;
}

const docLabels: Record<Doc['kind'], string> = {
  dec_page: 'Renewal dec page',
  prior_dec_page: "Last term's dec page",
  id_cards: 'ID cards',
  invoice: 'Carrier invoice',
  other: 'File',
  notice: 'Notice',
};

export const docLabel = (doc: Doc) => docLabels[doc.kind] ?? 'File';

/** The title of the closed list that holds last term's dec page, in step 1 and in Files. */
export const lastTermTitle = "Last term's dec page (for comparison)";

const docOrder: Record<Doc['kind'], number> = {
  dec_page: 0,
  id_cards: 1,
  invoice: 2,
  other: 3,
  notice: 3,
  prior_dec_page: 4,
};

/**
 * The record's files, in the order the VA works them (Yehuda, Oct 28): this term's on top (the
 * renewal dec page, then the ID cards, then the rest), and last term's dec page apart. That one is
 * only there so the portal can compare with last year, so it sits in a closed list below and is
 * never attached to the insured's email.
 */
export function splitDocs<D extends Pick<Doc, 'kind'>>(docs: D[]): { current: D[]; lastTerm: D[] } {
  const rank = (d: D) => docOrder[d.kind] ?? docOrder.other;
  const current = docs
    .filter((d) => d.kind !== 'prior_dec_page')
    .map((d, i) => ({ d, i }))
    .sort((a, b) => rank(a.d) - rank(b.d) || a.i - b.i)
    .map(({ d }) => d);
  return { current, lastTerm: docs.filter((d) => d.kind === 'prior_dec_page') };
}

/** "Attempt 1 of 2" under a failed attempt that may be tried again. */
export const attemptLine = (failures: number, of: number) => `Attempt ${failures} of ${of}`;

/** A sentence ends with one period, also after a name like "Rubin, G.". */
const sentence = (s: string) => (s.endsWith('.') ? s : `${s}.`);

const dayCount = (n: number) => (n === 1 ? '1 day' : `${n} days`);

/**
 * The Dec page step when the renewal is not on the carrier site yet (Rachy, Oct 28). Before the
 * carrier usually shows renewals (its stored days) that is expected; after it, check again
 * tomorrow; from the follow-up days (Settings, Defaults), call the carrier. A carrier with no
 * stored days keeps the plain line.
 */
export function notOnSite(
  name: string,
  view: Pick<FetchView, 'followUp' | 'carrierShows'>,
): { text: string; expected: boolean } {
  const { followUp, carrierShows: shows } = view;
  const at = `${dayCount(followUp.days)} before the renewal date`;
  const call = `If it is still not there ${at}, call ${name}.`;
  const usually =
    shows && `${name} usually shows renewals ${dayCount(shows.days)} before the renewal date`;
  if (followUp.due) {
    return {
      text: `Not available yet: ${name} does not show the renewal term, and it is ${at} or closer. Call ${name}.`,
      expected: false,
    };
  }
  if (shows?.expected) {
    return {
      text: `Not available yet, as expected: ${usually}. Check again then. ${call}`,
      expected: true,
    };
  }
  if (shows) {
    return {
      text: `Not available yet: ${usually}, but it does not show this one yet. Check again tomorrow. ${call}`,
      expected: false,
    };
  }
  return {
    text: `Not available yet: ${name} does not show the renewal term. Check again tomorrow. ${call}`,
    expected: false,
  };
}

/** The message on any page when one of my fetches finishes. */
export function finishedText(f: FinishedFetch): string {
  if (f.status === 'failed' && f.siteDown) {
    return sentence(`The carrier site was down. Nothing fetched for ${f.insured}`);
  }
  if (f.status === 'failed' && f.notConfigured) {
    return sentence(`Fetching is not set up here. Nothing fetched for ${f.insured}`);
  }
  if (f.status === 'failed') return sentence(`Fetch failed for ${f.insured}`);
  if (f.notAvailable)
    return sentence(`No renewal dec page on the carrier site yet for ${f.insured}`);
  return sentence(`Dec page fetched for ${f.insured}`);
}

/** The step's view after a press of Fetch that the server refused (409) or that failed. */
export const startFailedText = 'The fetch could not start. Try again.';

/** Why step 1's Delete is off (S17): a step after it is ticked, so Replace keeps the steps. */
export const decDeleteOff =
  'A step after the dec page is ticked. Use Replace instead: the steps stay as they are.';

/** Why step 1's Delete or its Undo did not go through, from the server's answer. */
export function decDeleteRefusal(error: string | undefined): string {
  switch (error) {
    case 'steps_ticked':
      return decDeleteOff;
    case 'reading_pending':
      return 'The dec page is still being read. Try again in a minute.';
    case 'cannot_undo':
      return 'Could not undo. 10 minutes passed, or the same file was added again.';
    case 'renewal_closed':
      return 'This renewal is closed now. Read it again.';
    default:
      return 'Could not delete the dec page. Read it again.';
  }
}
